'use client';
import { cn } from '@/lib/utils';

// The apps aren't published yet, so we have no store URLs. Drop them in here
// when they exist — each badge turns itself into a real link (and the "coming
// soon" caption disappears) as soon as both URLs are non-empty. No other file
// needs to change.
export const APP_STORE_URL = '';
export const PLAY_STORE_URL = '';

function AppleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-7 w-7 shrink-0 fill-current"
      aria-hidden="true"
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GooglePlayIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6 shrink-0 fill-current"
      aria-hidden="true"
    >
      <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
    </svg>
  );
}

const badges = [
  {
    key: 'ios',
    href: APP_STORE_URL,
    lead: 'Download on the',
    name: 'App Store',
    icon: <AppleIcon />,
  },
  {
    key: 'android',
    href: PLAY_STORE_URL,
    lead: 'Get it on',
    name: 'Google Play',
    icon: <GooglePlayIcon />,
  },
];

const badgeClass =
  'inline-flex items-center gap-3 rounded-xl border border-[#231f20] bg-[#231f20] px-4 py-2.5 text-white';

export function AppStoreBadges({
  className,
  centered = false,
  label,
}: {
  className?: string;
  centered?: boolean;
  label?: string;
}) {
  const isLive = badges.every((badge) => !!badge.href);

  return (
    <div className={cn('font-merriweather', centered && 'text-center', className)}>
      {label && (
        <p className="font-cinzel mb-4 text-[11px] font-semibold tracking-[0.22em] text-[#554E49] uppercase">
          {label}
        </p>
      )}

      <div
        className={cn(
          'flex flex-wrap items-center gap-3',
          centered && 'justify-center'
        )}
      >
        {badges.map((badge) => {
          const content = (
            <>
              {badge.icon}
              <span className="flex flex-col items-start leading-none">
                <span className="text-[9px] tracking-[0.08em] uppercase opacity-80">
                  {badge.lead}
                </span>
                <span className="font-cinzel mt-1 text-[15px] font-bold">
                  {badge.name}
                </span>
              </span>
            </>
          );

          // Until the store URLs exist the badge is a non-interactive
          // placeholder — visible, but it doesn't pretend to be a link.
          return badge.href ? (
            <a
              key={badge.key}
              href={badge.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${badge.lead} ${badge.name}`}
              className={cn(
                badgeClass,
                'transition-colors hover:bg-[#3c3733] hover:border-[#3c3733]'
              )}
            >
              {content}
            </a>
          ) : (
            <span
              key={badge.key}
              aria-label={`${badge.name} — coming soon`}
              // opacity-60 dropped the nested opacity-80 lead text to 3.8:1
              // against the composited badge; 75% keeps it above 4.5:1.
              className={cn(badgeClass, 'cursor-default opacity-75')}
            >
              {content}
            </span>
          );
        })}
      </div>

      {!isLive && (
        <p
          className={cn(
            'mt-3 text-[12px] text-[#5B493C]',
            centered && 'text-center'
          )}
        >
          Coming soon to iOS and Android.
        </p>
      )}
    </div>
  );
}
